import {
  ArrowRight,
  Clock,
  Heart,
  MapPin,
  Repeat,
  ShareNetwork,
  Users,
} from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { categoryCardUrl } from '@/lib/category-covers'
import { localizedCategoryName } from '@/lib/category-labels'
import { resolveMediaUrl } from '@/lib/media'
import { formatDate, formatNumber, formatTime } from '@/lib/i18n/format'
import { activityShareUrl } from '@/features/activity-detail/links'

import { useAppLocale } from '../hooks/ui'
import type { Activity } from '../types'

interface ActivityCardProps {
  activity: Activity
  onToggleSave?: (activity: Activity) => void
  savePending?: boolean
  showSave?: boolean
}

function viewerBadgeKey(status: Activity['viewerStatus']): string | null {
  if (status === 'creator') return 'discovery.card.creator'
  if (status === 'joined' || status === 'attended') return 'discovery.card.joined'
  if (status === 'pending') return 'discovery.card.pending'
  if (status === 'waitlisted') return 'discovery.card.waitlisted'
  return null
}

function isOngoing(activity: Activity, now: number): boolean {
  if (!activity.startAt || !activity.endAt) return false
  const start = Date.parse(activity.startAt)
  const end = Date.parse(activity.endAt)
  return Number.isFinite(start) && Number.isFinite(end) && start <= now && now < end
}

function weekdayName(
  weekday: number | null | undefined,
  t: (key: string) => string,
): string | null {
  if (weekday == null || weekday < 1 || weekday > 7) return null
  const keys = [
    'common.weekdays.monday',
    'common.weekdays.tuesday',
    'common.weekdays.wednesday',
    'common.weekdays.thursday',
    'common.weekdays.friday',
    'common.weekdays.saturday',
    'common.weekdays.sunday',
  ]
  return t(keys[weekday - 1])
}

function categoryTint(color: string): string {
  // 25% category tint over the paper background, matching the upstream pill
  // without losing the Deep foreground contrast.
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}40` : 'var(--mint)'
}

export function ActivityCard({
  activity,
  onToggleSave,
  savePending = false,
  showSave = true,
}: ActivityCardProps) {
  const { t } = useTranslation()
  const locale = useAppLocale()
  const [now, setNow] = useState(0)
  const [shareNotice, setShareNotice] = useState<'copied' | 'error' | null>(null)

  useEffect(() => {
    if (!shareNotice) return
    const timer = window.setTimeout(() => setShareNotice(null), 2500)
    return () => window.clearTimeout(timer)
  }, [shareNotice])

  useEffect(() => {
    const update = () => setNow(Date.now())
    const initial = window.setTimeout(update, 0)
    const timer = window.setInterval(update, 60_000)
    return () => {
      window.clearTimeout(initial)
      window.clearInterval(timer)
    }
  }, [])

  const cover =
    resolveMediaUrl(activity.coverImageUrl) ||
    categoryCardUrl(activity.categorySlug, activity.categoryName)
  const badgeKey = viewerBadgeKey(activity.viewerStatus)
  const ongoing = now > 0 && isOngoing(activity, now)
  const location = [activity.locationName, activity.city]
    .filter(Boolean)
    .join(' · ')
  const startDate = activity.startAt ? formatDate(activity.startAt, locale) : ''
  const startTime = activity.startAt ? formatTime(activity.startAt, locale) : ''
  const recurrence = activity.isRecurring
    ? weekdayName(activity.recurrenceWeekday, t)
    : null

  let price = t('discovery.card.priceExpenses')
  if (activity.priceType === 'free') {
    price = t('discovery.card.priceFree')
  } else if (activity.estimatedPrice !== null) {
    price = t('discovery.card.priceEstimate', {
      price: formatNumber(activity.estimatedPrice, locale, {
        maximumFractionDigits: 2,
      }),
    })
  }

  async function share(): Promise<void> {
    const url = activityShareUrl(activity.id, activity.inviteToken)
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: activity.title, url })
        return
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
      }
    }
    try {
      await navigator.clipboard.writeText(url)
      setShareNotice('copied')
    } catch {
      setShareNotice('error')
    }
  }

  return (
    <article className="activity-card relative">
      <Link
        to={`/activity/${activity.id}`}
        className="absolute inset-0 z-10"
        aria-label={t('discovery.card.open', { title: activity.title })}
      />

      <div className="activity-card__cover">
        {cover ? (
          <img src={cover} alt="" loading="lazy" aria-hidden="true" />
        ) : null}
        {ongoing ? (
          <span className="activity-card__badge activity-card__badge--live absolute bottom-2 left-2">
            {t('discovery.card.ongoing')}
          </span>
        ) : null}
      </div>

      <div className="activity-card__body">
        <div className="flex min-h-9 items-start gap-2 pr-[5.4rem]">
          <p className="activity-card__title min-w-0 flex-1">{activity.title}</p>
        </div>

        <p className="mt-1 flex flex-wrap gap-1">
          <span
            className="inline-flex max-w-full items-center truncate rounded-pill px-2 py-0.5 text-[12px] font-bold text-deep"
            style={{ backgroundColor: categoryTint(activity.categoryColor) }}
          >
            {localizedCategoryName(t, activity.categorySlug, activity.categoryName)}
          </span>
          {badgeKey ? (
            <span className="rounded-pill bg-mint px-2 py-0.5 text-[11px] font-bold text-teal">
              {t(badgeKey)}
            </span>
          ) : null}
        </p>

        {location ? (
          <p className="mt-1 flex items-center gap-1.5 text-[13px] text-muted">
            <MapPin size={15} weight="bold" aria-hidden="true" />
            <span className="truncate">{location}</span>
          </p>
        ) : null}

        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted">
          {startDate ? (
            <span className="inline-flex items-center gap-1.5">
              <Clock size={15} weight="bold" aria-hidden="true" />
              <span>
                {startDate} · {startTime}
              </span>
            </span>
          ) : null}
          {recurrence ? (
            <span className="inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-[11px] font-bold text-coral" style={{ backgroundColor: 'rgb(255 111 89 / 0.12)' }}>
              <Repeat size={12} weight="bold" aria-hidden="true" />
              {t('common.everyWeekday', { weekday: recurrence })}
            </span>
          ) : null}
        </div>

        <p className="mt-1 flex items-center gap-1.5 text-[13px] text-muted">
          <Users size={15} weight="bold" aria-hidden="true" />
          {t('discovery.card.participantsSpots', {
            current: formatNumber(activity.currentParticipantsCount, locale),
            max: formatNumber(activity.maxParticipants, locale),
            spots: formatNumber(activity.availableSpots, locale),
          })}
        </p>

        <div className="mt-1.5 flex items-center gap-3">
          <p className="min-w-0 flex-1 truncate text-[15px] font-bold text-deep">
            {price}
          </p>
          <span className="inline-flex items-center gap-1 text-[15px] font-bold text-teal">
            {t('discovery.card.details')}
            <ArrowRight size={16} weight="bold" aria-hidden="true" />
          </span>
        </div>
      </div>

      <div className="absolute top-1 right-1 z-20 flex gap-1.5">
        <button
          type="button"
          className="grid size-9 place-items-center rounded-full bg-white/70 text-muted"
          aria-label={t('activityDetail.actions.share')}
          onClick={() => void share()}
        >
          <ShareNetwork size={16} weight="bold" aria-hidden="true" />
        </button>
        {showSave ? (
          <button
            type="button"
            className="grid size-9 place-items-center rounded-full bg-white/70"
            aria-pressed={activity.saved}
            aria-busy={savePending}
            disabled={savePending}
            onClick={() => onToggleSave?.(activity)}
          >
            <Heart
              size={16}
              weight={activity.saved ? 'fill' : 'bold'}
              aria-hidden="true"
              className={activity.saved ? 'text-teal' : 'text-muted'}
            />
            <span className="sr-only">
              {activity.saved
                ? t('discovery.card.unsave')
                : t('discovery.card.save')}
            </span>
          </button>
        ) : null}
      </div>

      <p role="status" className="sr-only">
        {shareNotice === 'copied'
          ? t('activityDetail.actions.shareCopied')
          : shareNotice === 'error'
            ? t('activityDetail.actions.shareCopyError')
            : ''}
      </p>
    </article>
  )
}

export function ActivityCardSkeleton() {
  return (
    <div
      className="activity-card animate-pulse"
      aria-hidden="true"
      data-testid="activity-skeleton"
    >
      <div className="activity-card__cover bg-line" />
      <div className="activity-card__body space-y-2">
        <div className="h-5 w-3/4 rounded bg-line" />
        <div className="h-4 w-1/2 rounded bg-line" />
        <div className="h-4 w-2/3 rounded bg-line" />
      </div>
    </div>
  )
}
